import { inject, Injectable } from '@angular/core';
import {
    CreateFieldResponseDTO,
    DocumentApi,
    DocumentEntity,
    DocumentItemConfigsPartial,
    DocumentItemPartial,
    DocumentItemTypeEnum,
    ResponseApiDto,
} from '@domain';
import {
    DialogConfirmationControl,
    RaDialogConfirmationService,
    RaToastService,
} from '@shared/components';
import { catchError, map, Observable, tap } from 'rxjs';
import { UtilService } from 'src/app/shared/utils/utils.service';
import { DomainTranslateService } from '../../utils/domain-translate.service';
import { OwnFieldRevisionsService } from './own-field-revisions.service';

@Injectable({
    providedIn: 'root',
})
export class DocumentFieldsController {
    // * [API]
    private _api = inject(DocumentApi);

    // * [Services]
    private _utilService = inject(UtilService);
    private _raToastService = inject(RaToastService);
    private _raDialogConfirmationService = inject(RaDialogConfirmationService);
    private _i18n = inject(DomainTranslateService);
    /** Revisões das escritas desta aba: base do editor de texto (ver o serviço). */
    private _ownRevs = inject(OwnFieldRevisionsService);

    /**
     * Create fields
     * @param documentId ID of document
     * @param sessionId ID of session
     * @param fields Fields to create
     * @returns Observable of ResponseApiDto<CreateFieldResponseDTO>
     */
    public createFields(
        documentId: string,
        sessionId: string,
        fields: DocumentItemPartial[],
        aboveFieldId?: string,
        belowFieldId?: string
    ): Observable<CreateFieldResponseDTO> {
        return this._api
            .createFields(documentId, sessionId, fields, aboveFieldId, belowFieldId)
            .pipe(
                map(response => response.body),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.errors.createFields')
                    )
                )
            );
    }

    /**
     * Confirma com o usuário a remoção dos campos.
     *
     * Separado do envio porque a remoção tem janela de desfazer: o dialog decide
     * SE remove, e o `remove-fields` só sai quando a janela expira (ver
     * `FieldRemovalService`).
     * @param fieldsCount Quantidade de campos (define singular/plural)
     */
    public confirmRemoveFields(fieldsCount: number): Observable<DialogConfirmationControl> {
        const title =
            fieldsCount > 1
                ? this._i18n.instant('domain.document.dialog.removeFieldsTitlePlural')
                : this._i18n.instant('domain.document.dialog.removeFieldsTitleSingular');
        const message =
            fieldsCount > 1
                ? this._i18n.instant('domain.document.dialog.removeFieldsMessagePlural')
                : this._i18n.instant('domain.document.dialog.removeFieldsMessageSingular');

        return this._raDialogConfirmationService.confirm({
            title: title,
            message: message,
            confirmText: this._i18n.instant('common.remove'),
            cancelText: this._i18n.instant('common.cancel'),
            type: 'danger',
        });
    }

    /**
     * Remove fields
     *
     * Só o envio: a confirmação é do `confirmRemoveFields` e o toast de sucesso
     * é o próprio toast de "Desfazer" mostrado pelo `FieldRemovalService`.
     * @param documentId ID of document
     * @param sessionId ID of session
     * @param fieldIds IDs of fields
     */
    public removeFields(
        documentId: string,
        sessionId: string,
        fieldIds: string[]
    ): Observable<void> {
        return this._api.removeFields(documentId, sessionId, fieldIds).pipe(
            map(() => void 0),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.removeFields')
                )
            )
        );
    }

    /**
     * Update text
     * @param documentId ID of document
     * @param sessionId ID of session
     * @param fieldId ID of field
     * @param text Text to update
     * @returns Observable of ResponseApiDto<DocumentEntity>
     */
    public updateText(
        documentId: string,
        sessionId: string,
        fieldId: string,
        baseText: string,
        newText: string,
        baseRev?: number
    ): Observable<DocumentEntity> {
        return this._api
            .updateText(documentId, sessionId, fieldId, baseText, newText, baseRev)
            .pipe(
                map(response => response.body),
                tap(document => this._ownRevs.record(document, fieldId)),
                catchError(error => {
                    // 409 = o campo mudou desde o início da edição. Não é erro de
                    // sistema: o texto digitado continua válido e o chamador o devolve
                    // ao editor, então a mensagem precisa ser a do conflito, não a
                    // genérica de falha.
                    const message =
                        error?.status === 409
                            ? this._i18n.instant('domain.document.errors.updateTextConflict')
                            : this._i18n.instant('domain.document.errors.updateText');

                    return this._utilService.handleError(error, message);
                })
            );
    }

    /**
     * Change the type of a field (TEXT/TITLE/SUBTITLE)
     * @param documentId ID of document
     * @param sessionId ID of session
     * @param fieldId ID of field
     * @param newFieldType New type of field
     * @returns Observable of ResponseApiDto<DocumentEntity>
     */
    public changeFieldType(
        documentId: string,
        sessionId: string,
        fieldId: string,
        newFieldType: DocumentItemTypeEnum
    ): Observable<DocumentEntity> {
        return this._api.changeFieldType(documentId, sessionId, fieldId, newFieldType).pipe(
            map(response => response.body),
            tap(document => this._ownRevs.record(document, fieldId)),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.changeFieldType')
                )
            )
        );
    }

    /**
     * Atualiza a configuração de UM item (imagem ou grupo de imagens).
     * Sem toast de sucesso: é auto-save enquanto o usuário mexe nas opções do card.
     * @param documentId ID do documento
     * @param sessionId ID da seção
     * @param fieldId ID do campo
     * @param configs Override do item (campos ausentes herdam a config do documento)
     * @returns Observable com o documento atualizado
     */
    public updateItemConfig(
        documentId: string,
        sessionId: string,
        fieldId: string,
        configs: DocumentItemConfigsPartial
    ): Observable<DocumentEntity> {
        return this._api.updateItemConfig(documentId, sessionId, fieldId, configs).pipe(
            map(response => response.body),
            tap(document => this._ownRevs.record(document, fieldId)),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.updateItemConfig')
                )
            )
        );
    }

    /**
     * Troca a lista de cards de uma grade de indicadores (item STATS). Sem toast de
     * sucesso: é auto-save enquanto o usuário edita os cards.
     */
    public updateStats(
        documentId: string,
        sessionId: string,
        fieldId: string,
        stats: { value: string; label: string }[],
        baseRev?: number
    ): Observable<DocumentEntity> {
        return this._api.updateStats(documentId, sessionId, fieldId, stats, baseRev).pipe(
            map(response => response.body),
            tap(document => this._ownRevs.record(document, fieldId)),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    error?.status === 409
                        ? this._i18n.instant('domain.document.errors.updateStatsConflict')
                        : this._i18n.instant('domain.document.errors.updateStats')
                )
            )
        );
    }

    /**
     * Troca o conteúdo de uma tabela (item TABLE). Sem toast de sucesso: é auto-save
     * enquanto o usuário edita as células.
     */
    public updateTable(
        documentId: string,
        sessionId: string,
        fieldId: string,
        table: { columns: string[]; rows: string[][] },
        baseRev?: number
    ): Observable<DocumentEntity> {
        return this._api.updateTable(documentId, sessionId, fieldId, table, baseRev).pipe(
            map(response => response.body),
            tap(document => this._ownRevs.record(document, fieldId)),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    error?.status === 409
                        ? this._i18n.instant('domain.document.errors.updateTableConflict')
                        : this._i18n.instant('domain.document.errors.updateTable')
                )
            )
        );
    }

    /**
     * Troca o gráfico inteiro (item CHART). Sem toast de sucesso: quem confirma é o
     * assistente fechando com o gráfico novo no lugar.
     */
    public updateChart(
        documentId: string,
        sessionId: string,
        fieldId: string,
        chart: unknown,
        baseRev?: number
    ): Observable<DocumentEntity> {
        return this._api.updateChart(documentId, sessionId, fieldId, chart, baseRev).pipe(
            map(response => response.body),
            tap(document => this._ownRevs.record(document, fieldId)),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    error?.status === 409
                        ? this._i18n.instant('domain.document.errors.updateChartConflict')
                        : this._i18n.instant('domain.document.errors.updateChart')
                )
            )
        );
    }

    /**
     * Troca o conteúdo de um semáforo (item SEMAPHORE). Sem toast de sucesso: é
     * auto-save enquanto o usuário avalia os itens.
     */
    public updateSemaphore(
        documentId: string,
        sessionId: string,
        fieldId: string,
        semaphore: unknown,
        baseRev?: number
    ): Observable<DocumentEntity> {
        return this._api.updateSemaphore(documentId, sessionId, fieldId, semaphore, baseRev).pipe(
            map(response => response.body),
            tap(document => this._ownRevs.record(document, fieldId)),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    error?.status === 409
                        ? this._i18n.instant('domain.document.errors.updateSemaphoreConflict')
                        : this._i18n.instant('domain.document.errors.updateSemaphore')
                )
            )
        );
    }

    /**
     * Move a seleção para uma seção nova (criada logo depois da atual). Devolve o documento
     * atualizado e o id da seção nova.
     */
    public moveFieldsIntoNewSession(
        documentId: string,
        currentSessionId: string,
        fieldIds: string[],
        name?: string
    ): Observable<{ document: DocumentEntity; sessionId: string }> {
        return this._api
            .moveFieldsIntoNewSession(documentId, currentSessionId, fieldIds, name)
            .pipe(
                map(response => {
                    this._raToastService.success({
                        message:
                            response.message[0] ||
                            this._i18n.instant('domain.document.success.fieldsMovedToNewSession'),
                    });
                    return response.body;
                }),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.errors.moveFieldsIntoNewSession')
                    )
                )
            );
    }

    /**
     * Move fields into session
     * @param documentId ID of document
     * @param currentSessionId ID of current session
     * @param targetSessionId ID of target session
     * @param fieldIds IDs of fields
     * @returns Observable of ResponseApiDto<DocumentEntity>
     */
    public moveFieldsIntoSession(
        documentId: string,
        currentSessionId: string,
        targetSessionId: string,
        fieldIds: string[],
        targetFolderId?: string
    ): Observable<DocumentEntity> {
        const errors = [];
        if (!documentId) errors.push(this._i18n.instant('domain.validation.moveFieldsDocumentId'));
        if (!currentSessionId)
            errors.push(this._i18n.instant('domain.validation.moveFieldsCurrentSessionId'));
        if (!targetSessionId)
            errors.push(this._i18n.instant('domain.validation.moveFieldsTargetSessionId'));
        if (!fieldIds) errors.push(this._i18n.instant('domain.validation.moveFieldsFieldIds'));
        if (errors.length > 0) throw new Error(errors.join('\n'));

        return this._api
            .moveFieldsIntoSessionFields(
                documentId,
                currentSessionId,
                targetSessionId,
                fieldIds,
                targetFolderId
            )
            .pipe(
                map((response: ResponseApiDto<DocumentEntity>) => {
                    // Mensagem de sucesso
                    this._raToastService.success({
                        message:
                            response.message[0] ||
                            this._i18n.instant('domain.document.success.fieldsMoved'),
                    });

                    return response.body;
                }),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.errors.moveFields')
                    )
                )
            );
    }

    /**
     * Realiza a correção ortográfica de um campo de texto
     * @param documentId ID do documento
     * @param sessionId ID da seção
     * @param fieldId ID do campo
     * @param iaAction Ação da IA
     * @returns Observable de ResponseApiDto<DocumentEntity>
     */
    public textCorrectionIa(
        documentId: string,
        sessionId: string,
        fieldId: string,
        text?: string
    ): Observable<DocumentEntity> {
        return this._api.textCorrectionIa(documentId, sessionId, fieldId, text).pipe(
            // O toast de sucesso é do chamador: ele carrega a ação "Desfazer"
            // que devolve o texto original do usuário.
            map(response => response.body),
            tap(document => this._ownRevs.record(document, fieldId)),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.spellcheck')
                )
            )
        );
    }

    /**
     * Realiza a correção ortográfica de um campo de texto
     * @param documentId ID do documento
     * @param sessionId ID da seção
     * @param fieldId ID do campo
     * @param iaAction Ação da IA
     * @returns Observable de ResponseApiDto<DocumentEntity>
     */
    public textPromptIA(
        documentId: string,
        sessionId: string,
        fieldId: string,
        text?: string
    ): Observable<DocumentEntity> {
        return this._api.textPromptIA(documentId, sessionId, fieldId, text).pipe(
            // O toast de sucesso é do chamador (ver textCorrectionIa).
            map(response => response.body),
            tap(document => this._ownRevs.record(document, fieldId)),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.spellcheck')
                )
            )
        );
    }

    /**
     * Gera uma descrição automatica para um campo de imagem
     * @param documentId ID do documento
     * @param sessionId ID da seção
     * @param fieldId ID do campo
     * @param text Texto para gerar a descrição
     * @returns Observable de ResponseApiDto<DocumentEntity>
     */
    public imagesDescriptionIa(
        documentId: string,
        sessionId: string,
        fieldId: string,
        text?: string
    ): Observable<DocumentEntity> {
        return this._api.imagesDescriptionIA(documentId, sessionId, fieldId, text).pipe(
            // O toast de sucesso é do chamador (ver textCorrectionIa).
            map(response => response.body),
            tap(document => this._ownRevs.record(document, fieldId)),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.imageDescriptionIa')
                )
            )
        );
    }

    /**
     * Realiza a correção ortográfica de um campo de texto
     * @param documentId ID do documento
     * @param sessionId ID da seção
     * @returns Observable de ResponseApiDto<DocumentEntity>
     */
    public allTextsCorrectionIa(
        documentId: string,
        sessionId?: string
    ): Observable<DocumentEntity> {
        return this._api.allTextsCorrectionIa(documentId, sessionId).pipe(
            map(response => {
                // Mensagem de sucesso
                this._raToastService.success({
                    message:
                        response.message[0] ||
                        this._i18n.instant('domain.document.success.spellcheckAllDone'),
                });

                return response.body;
            }),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.spellcheckAll')
                )
            )
        );
    }

    /**
     * Altera o valor de um campo de imagem
     * @param documentId ID do documento
     * @param sessionId ID da seção
     * @param fieldId ID do campo
     * @param newImageUrl URL da nova imagem
     * @param newImageSize Tamanho da nova imagem
     * @returns Observable de ResponseApiDto<DocumentEntity>
     */
    /**
     * Altera a data de uma imagem única.
     * @returns Observable do DocumentEntity atualizado
     */
    public changeImageDate(
        documentId: string,
        sessionId: string,
        itemId: string,
        date: Date
    ): Observable<DocumentEntity> {
        return this._api
            .changeDateImageField(documentId, sessionId, itemId, date.toISOString())
            .pipe(
                map(response => {
                    this._raToastService.success({
                        message:
                            response.message[0] ||
                            this._i18n.instant('domain.document.success.dateChanged'),
                    });

                    return response.body;
                }),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.errors.changeDate')
                    )
                )
            );
    }

    /**
     * Altera a data de uma foto de um grupo de imagens.
     * @param mediaId Id da mídia (value[]._id); precede `position` quando presente
     * @returns Observable do DocumentEntity atualizado
     */
    public changeGroupImageDate(
        documentId: string,
        sessionId: string,
        itemId: string,
        position: number,
        date: Date,
        mediaId?: string
    ): Observable<DocumentEntity> {
        return this._api
            .changeDateImageFromGroupField(
                documentId,
                sessionId,
                itemId,
                position,
                date.toISOString(),
                mediaId
            )
            .pipe(
                map(response => {
                    this._raToastService.success({
                        message:
                            response.message[0] ||
                            this._i18n.instant('domain.document.success.dateChanged'),
                    });

                    return response.body;
                }),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.errors.changeDate')
                    )
                )
            );
    }

    public changeImageField(
        documentId: string,
        sessionId: string,
        fieldId: string,
        newImageUrl: string,
        newImageSize: number
    ): Observable<DocumentEntity> {
        return this._api
            .changeImageField(documentId, sessionId, fieldId, newImageUrl, newImageSize)
            .pipe(
                map(response => {
                    // Mensagem de sucesso
                    this._raToastService.success({
                        message:
                            response.message[0] ||
                            this._i18n.instant('domain.document.success.imageChanged'),
                    });

                    return response.body;
                }),
                tap(document => this._ownRevs.record(document, fieldId)),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.errors.changeImage')
                    )
                )
            );
    }

    /**
     * Altera o valor de um campo de grupo de mídias
     * @param documentId ID do documento
     * @param sessionId ID da seção
     * @param fieldId ID do campo
     * @param items Itens a serem alterados
     * @returns Observable de ResponseApiDto<DocumentEntity>
     */
    public changeMediaFromGroupField(
        documentId: string,
        sessionId: string,
        fieldId: string,
        items: {
            position: number;
            newImageUrl: string;
            newImageSize: number;
            /**
             * `_id` da mídia no grupo. Endereço estável: a posição desloca quando
             * outra pessoa mexe no grupo com a tela aberta. Opcional (grupos
             * antigos só ganham `_id` na primeira escrita feita no backend).
             */
            mediaId?: string;
        }[]
    ): Observable<DocumentEntity> {
        return this._api.changeMediaFromGroupField(documentId, sessionId, fieldId, items).pipe(
            map(response => {
                // Mensagem de sucesso
                this._raToastService.success({
                    message:
                        response.message[0] ||
                        this._i18n.instant('domain.document.success.groupMediaChanged'),
                });

                return response.body;
            }),
            tap(document => this._ownRevs.record(document, fieldId)),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.groupMedia')
                )
            )
        );
    }

    /**
     * Remove uma imagem de um campo de grupo de mídias
     * @param documentId ID do documento
     * @param sessionId ID da seção
     * @param fieldId ID do campo
     * @param position Posição da imagem
     * @returns Observable de ResponseApiDto<DocumentEntity>
     */
    public removeImageFromGroupField(
        documentId: string,
        sessionId: string,
        fieldId: string,
        positions: number[],
        mediaIds?: string[]
    ): Observable<DocumentEntity> {
        return this._api
            .removeImageFromGroupField(documentId, sessionId, fieldId, positions, mediaIds)
            .pipe(
                map(response => {
                    // Mensagem de sucesso
                    this._raToastService.success({
                        message:
                            response.message[0] ||
                            this._i18n.instant('domain.document.success.imageRemovedFromGroup'),
                    });

                    return response.body;
                }),
                tap(document => this._ownRevs.record(document, fieldId)),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.errors.removeImage')
                    )
                )
            );
    }

    /**
     * Duplica um campo logo abaixo dele. O card novo chega pelo evento de campo criado
     * (o mesmo do favorito); aqui só o aviso de sucesso ou de erro.
     */
    public duplicateField(
        documentId: string,
        sessionId: string,
        fieldId: string
    ): Observable<string | undefined> {
        return this._api.duplicateField(documentId, sessionId, fieldId).pipe(
            map(response => {
                this._raToastService.success({
                    message:
                        response.message[0] ||
                        this._i18n.instant('domain.document.success.fieldDuplicated'),
                });
                return response.body?.fieldsId?.[0];
            }),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.duplicateField')
                )
            )
        );
    }

    /**
     * Adiciona um campo favorito a um documento
     * @param documentId ID do documento
     * @param sessionId ID da seção
     * @param favoriteFieldId ID do campo favorito
     * @param aboveFieldId ID do campo acima
     * @param belowFieldId ID do campo abaixo
     * @param showToast Exibe o toast de sucesso (false quando o chamador adiciona
     * vários em sequência e mostra um único toast ao final)
     * @returns Observable de ResponseApiDto<DocumentEntity>
     */
    public addFavoriteField(
        documentId: string,
        sessionId: string,
        favoriteFieldId: string,
        aboveFieldId?: string,
        belowFieldId?: string,
        folderItemId?: string,
        showToast: boolean = true
    ): Observable<DocumentEntity> {
        return this._api
            .addFavoriteField(
                documentId,
                sessionId,
                favoriteFieldId,
                aboveFieldId,
                belowFieldId,
                folderItemId
            )
            .pipe(
                map(response => {
                    // Mensagem de sucesso
                    if (showToast) {
                        this._raToastService.success({
                            message:
                                response.message[0] ||
                                this._i18n.instant('domain.document.success.favoriteFieldAdded'),
                        });
                    }

                    return response.body;
                }),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.errors.addFavoriteField')
                    )
                )
            );
    }

    /**
     * Altera o valor de um campo de grupo de mídias
     * @param documentId ID do documento
     * @param sessionId ID da seção
     * @param fieldId ID do campo
     * @param newImageUrl URL da nova imagem
     * @param newImageSize Tamanho da nova imagem
     * @returns Observable de ResponseApiDto<DocumentEntity>
     */
    public addImagesToField(
        documentId: string,
        sessionId: string,
        fieldId: string,
        images: { url: string; size: number }[]
    ): Observable<DocumentEntity> {
        return this._api.addImageToField(documentId, sessionId, fieldId, images).pipe(
            map(response => {
                // Mensagem de sucesso
                this._raToastService.success({
                    message:
                        response.message[0] ||
                        this._i18n.instant('domain.document.success.imageAddedToField'),
                });

                return response.body;
            }),
            tap(document => this._ownRevs.record(document, fieldId)),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.addImagesToField')
                )
            )
        );
    }

    /**
     * Reorder fields
     * @param documentId ID do documento
     * @param sessionId ID da seção
     * @param sourceIndex Source index of field
     * @param targetIndex Target index of field
     * @returns Observable of void
     */
    public reorderFields(
        documentId: string,
        sessionId: string,
        sourceIndex: number,
        targetIndex: number
    ): Observable<void> {
        return this._api.reorderFields(documentId, sessionId, sourceIndex, targetIndex).pipe(
            map(() => void 0),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.reorderFields')
                )
            )
        );
    }
}
